// src/base/SliderNavigation.ts
var SliderNavigation = class {
  constructor(instance) {
    this.buttonPrev = instance.config.target.querySelector(".button-prev");
    this.buttonNext = instance.config.target.querySelector(".button-next");
    if (this.buttonPrev && this.buttonNext) {
      this.buttonPrev.addEventListener("click", () => instance.prevItem());
      this.buttonNext.addEventListener("click", () => instance.nextItem());
    }
  }
  updateNavigation(prevEnabled, nextEnabled) {
    if (this.buttonPrev && this.buttonNext) {
      this.buttonPrev.disabled = !prevEnabled;
      this.buttonNext.disabled = !nextEnabled;
    }
  }
};

// src/base/SliderPagination.ts
var SliderPagination = class {
  constructor(instance) {
    this.slider = instance;
    this.paginationContainer = this.slider.config.target.querySelector(
      ".scroll-slider-pagination"
    );
    if (this.paginationContainer !== null) {
      window.addEventListener("resize", () => this.handleResize(), { passive: true });
    }
  }
  renderPagination() {
    if (this.paginationContainer === null) {
      return;
    }
    this.paginationContainer.innerHTML = "";
    this.numBullets = this.calculateTotalBullets();
    let bullet;
    for (let i = 0; i < this.numBullets; i++) {
      bullet = document.createElement("button");
      bullet.classList.add("bullet");
      bullet.setAttribute("aria-label", `Slide ${i + 1}`);
      bullet.addEventListener("click", () => {
        this.slider.currentItem = i;
        this.slider.scrollToCurrentItem();
      });
      this.paginationContainer.appendChild(bullet);
    }
    this.paginationItems = this.paginationContainer.querySelectorAll(".bullet");
  }
  calculateTotalBullets() {
    const numBullets = this.slider.totalItems - (this.slider.itemsPerView - 1);
    if (this.slider.config.itemsPerView === -1) {
      return numBullets - Math.floor(this.slider.wrapper.offsetWidth / this.slider.itemSize);
    }
    return numBullets;
  }
  updatePagination(currentItem) {
    var _a;
    (_a = this.paginationItems) == null ? void 0 : _a.forEach((bullet, index) => {
      if (index === currentItem) {
        bullet.classList.add("active");
      } else {
        bullet.classList.remove("active");
      }
    });
  }
  handleResize() {
    if (this.paginationContainer === null) {
      return;
    }
    const numBullets = this.calculateTotalBullets();
    if (this.numBullets !== numBullets) {
      this.numBullets = numBullets;
      this.renderPagination();
    }
  }
};

// src/base/SliderBreakpointManager.ts
var SliderBreakpointManager = class {
  constructor(instance) {
    this.slider = instance;
    this.breakpointKeys = Object.keys(this.slider.config.breakpoints).sort(
      (a, b) => {
        return parseInt(a, 10) - parseInt(b, 10);
      }
    );
    window.addEventListener("resize", () => this.handleBreakpoints(), { passive: true });
    this.handleBreakpoints();
  }
  getCurrentBreakpoint() {
    const currentWidth = window.innerWidth;
    let currentBreakpointKey = "";
    for (let i = this.breakpointKeys.length - 1; i >= 0; i -= 1) {
      if (parseInt(this.breakpointKeys[i], 10) <= currentWidth) {
        currentBreakpointKey = this.breakpointKeys[i];
        break;
      }
    }
    return this.slider.config.breakpoints ? this.slider.config.breakpoints[currentBreakpointKey] : void 0;
  }
  handleBreakpoints() {
    const currentBreakpoint = this.getCurrentBreakpoint();
    const itemsPerView = currentBreakpoint !== void 0 ? Math.abs(currentBreakpoint.itemsPerView) : Math.abs(this.slider.config.itemsPerView);
    if (this.slider.itemsPerView !== itemsPerView) {
      this.slider.itemsPerView = itemsPerView;
      this.slider.pagination.renderPagination();
    }
  }
};

// src/base/SliderBase.ts
var SliderBase = class {
  constructor(config) {
    this.config = config;
    this.overflow = false;
    this.currentItem = 0;
    this.isScrolling = 0;
    this.itemsPerView = Math.abs(this.config.itemsPerView);
    this.container = this.config.target.querySelector(".scroll-slider");
    this.wrapper = this.container.querySelector(".scroll-slider-wrapper");
    this.items = this.container.querySelectorAll(".scroll-slider-wrapper > *");
    this.totalItems = this.items.length;
    this.itemSize = this.items[0].offsetWidth + this.getRowGap();
    this.navigation = new SliderNavigation(this);
    this.pagination = new SliderPagination(this);
    this.pagination.renderPagination();
    if (this.config.loop) {
      this.navigation.updateNavigation(true, true);
    }
    if (this.config.breakpoints) {
      new SliderBreakpointManager(this);
    }
    this.wrapper.addEventListener("scroll", () => this.handleScroll(), { passive: true });
    window.addEventListener("resize", () => this.handleResize(), { passive: true });
    this.handleResize();
  }
  getRowGap() {
    let rowGap = getComputedStyle(this.wrapper).rowGap;
    return rowGap === "normal" ? 0 : parseInt(rowGap);
  }
  scrollToCurrentItem(scrollBehavior = "smooth") {
    this.pagination.updatePagination(this.currentItem);
    this.wrapper.scrollTo({
      left: this.items[this.currentItem].offsetLeft,
      top: 0,
      behavior: scrollBehavior
    });
  }
  prevItem() {
    this.currentItem--;
    if (this.currentItem < 0) {
      this.currentItem = this.config.loop ? this.totalItems - 1 : 0;
    }
    this.scrollToCurrentItem();
  }
  nextItem() {
    this.currentItem++;
    const maxItems = this.totalItems - (this.itemsPerView - 1) - 1;
    if (this.currentItem > maxItems) {
      this.currentItem = this.config.loop ? 0 : maxItems;
    }
    this.scrollToCurrentItem();
  }
  handleScroll() {
    if (!this.config.loop) {
      const prevEnabled = !this.isScrollStart();
      const nextEnabled = !this.isScrollEnd();
      this.navigation.updateNavigation(prevEnabled, nextEnabled);
    }
    window.clearTimeout(this.isScrolling);
    this.isScrolling = window.setTimeout(() => {
      this.currentItem = this.calculateCurrentItem();
      this.pagination.updatePagination(this.currentItem);
    }, 66);
  }
  isScrollStart() {
    return this.wrapper.scrollLeft === 0;
  }
  isScrollEnd() {
    return Math.round(this.wrapper.scrollLeft) >= Math.round(this.wrapper.scrollWidth - this.wrapper.clientWidth);
  }
  calculateCurrentItem() {
    return Math.round(this.wrapper.scrollLeft / this.itemSize);
  }
  handleResize() {
    this.overflow = this.wrapper.scrollWidth > this.wrapper.clientWidth;
    this.itemSize = this.items[0].offsetWidth + this.getRowGap();
    if (this.overflow) {
      this.container.classList.add("scroll-enabled");
    } else {
      this.container.classList.remove("scroll-enabled");
      this.currentItem = 0;
      this.scrollToCurrentItem("auto");
    }
    this.handleScroll();
  }
};
export {
  SliderBase as Slider
};
//# sourceMappingURL=index.mjs.map